import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faEye, faTrash, faXmark } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Spinner from "react-activity/dist/Spinner";
import "react-activity/dist/Spinner.css";
import { apiUrl } from "../../../config/Config";
import { formatIstFullDateTime } from "../../../utils/dateTime";
import PostPreviewModal from "../../common/PostPreviewModal";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const Moderation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [selectedTab, setSelectedTab] = useState("blocked");
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [contentReports, setContentReports] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showloding, setshowloding] = useState(false);

  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  const headers = useMemo(() => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  }), [userdata?.token]);

  const handleInvalidUser = useCallback((message) => {
    if (message === "invalid user") {
      dispatch({ type: "userdata", payload: {} });
      navigate("/admin/login");
      return true;
    }
    return false;
  }, [dispatch, navigate]);

  const fetchBlockedUsers = useCallback(async () => {
    setshowloding(true);
    await axios
      .get(apiUrl + "admingetblockedusers", headers)
      .then((res) => {
        if (res.data.status === "success") {
          setBlockedUsers(res.data.data || []);
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
    setshowloding(false);
  }, [handleInvalidUser, headers]);

  const fetchContentReports = useCallback(async () => {
    setshowloding(true);
    await axios
      .get(apiUrl + "admingetcontentreports?status=pending", headers)
      .then((res) => {
        if (res.data.status === "success") {
          setContentReports(res.data.data || []);
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
    setshowloding(false);
  }, [handleInvalidUser, headers]);

  const handleReportAction = async (report, action) => {
    let adminMessage = "";
    const actionText = action === "remove_content" ? "hide/de-activate this post and notify the post owner" : "dismiss this report";
    const result = window.confirm("Are you sure you want to " + actionText + "?");
    if (!result) {
      return;
    }

    if (action === "remove_content") {
      adminMessage = window.prompt("Message to send to the post owner", "Your post was hidden because it was reported and violated community guidelines.");
      if (!adminMessage || !adminMessage.trim()) {
        alert("Please add a message before hiding this post.");
        return;
      }
    }

    setshowloding(true);
    await axios
      .post(apiUrl + "adminhandlecontentreport", {
        report_id: report.id,
        action,
        send_email: action === "remove_content" ? "true" : "false",
        admin_message: adminMessage,
      }, headers)
      .then((res) => {
        if (res.data.status === "success") {
          fetchContentReports();
        } else if (!handleInvalidUser(res.data.message)) {
          alert(res.data.message);
        }
      })
      .catch((err) => {
        alert(err.message);
      });
    setshowloding(false);
  };

  const dateCell = (props, field) => {
    const value = props?.data?.[field];
    return value ? formatIstFullDateTime(value) + " IST" : "-";
  };

  const blockedColumns = [
    { field: "blocked_user_name", headerName: "Blocked User", minWidth: 180 },
    { field: "blocked_user_email", headerName: "Blocked Email", minWidth: 220 },
    { field: "blocker_name", headerName: "Blocked By", minWidth: 180 },
    { field: "blocker_email", headerName: "Blocked By Email", minWidth: 220 },
    {
      field: "blocked_at",
      headerName: "Date",
      minWidth: 170,
      cellRenderer: (props) => dateCell(props, "blocked_at"),
    },
  ];

  const reportColumns = [
    { field: "content_type", headerName: "Type", minWidth: 110 },
    { field: "reporter_name", headerName: "Reported By", minWidth: 170 },
    { field: "reported_user_name", headerName: "Post Owner", minWidth: 170 },
    { field: "reported_user_email", headerName: "Owner Email", minWidth: 220 },
    { field: "post_text", headerName: "Post", minWidth: 260 },
    { field: "reason", headerName: "Reason", minWidth: 220 },
    {
      field: "created_at",
      headerName: "Reported Date",
      minWidth: 170,
      cellRenderer: (props) => dateCell(props, "created_at"),
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 150,
      cellRenderer: (props) => (
        <div className="moderation-action-row">
          <button
            className="moderation-icon-btn"
            title="View post"
            onClick={() => setSelectedPost(props.data)}
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
          <button
            className="moderation-icon-btn danger"
            title="Hide/de-activate post and email owner"
            onClick={() => handleReportAction(props.data, "remove_content")}
          >
            <FontAwesomeIcon icon={faTrash} />
            <FontAwesomeIcon icon={faEnvelope} />
          </button>
          <button
            className="moderation-icon-btn"
            title="Dismiss report"
            onClick={() => handleReportAction(props.data, "dismiss")}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchBlockedUsers();
    fetchContentReports();
  }, [fetchBlockedUsers, fetchContentReports]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "reports") {
      setSelectedTab("reports");
    }
  }, [location.search]);

  return (
    <div>
      <div className="dashboardbox" style={{ width: "100%" }}>
        <div className="productstogglebar mt-2">
          <div className="orderstatustogglediv">
            <div
              className={selectedTab === "blocked" ? "orderstatusdiv active" : "orderstatusdiv"}
              onClick={() => setSelectedTab("blocked")}
            >
              <p>Blocked Users</p>
            </div>
            <div
              className={selectedTab === "reports" ? "orderstatusdiv active" : "orderstatusdiv"}
              onClick={() => setSelectedTab("reports")}
            >
              <p>Content Reports</p>
            </div>
          </div>
          {showloding ? (
            <div className="moderation-loader">
              <Spinner color="#34A853" size={18} />
            </div>
          ) : null}
        </div>

        <div className="ag-theme-quartz mt-4" style={{ width: "100%", height: 550 }}>
          <AgGridReact
            rowData={selectedTab === "blocked" ? blockedUsers : contentReports}
            columnDefs={selectedTab === "blocked" ? blockedColumns : reportColumns}
            defaultColDef={{ flex: 1, filter: true, sortable: true, resizable: true }}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            rowSelection={"multiple"}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={paginationPageSizeSelector}
            rowHeight={54}
          />
        </div>
      </div>
      <PostPreviewModal post={selectedPost} onClose={() => setSelectedPost(null)} />
    </div>
  );
};

export default Moderation;
